import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import GroupedColumnPickerPanel, { ColumnPickerGroup, useCloseOnOutsideClick } from './GroupedColumnPickerPanel';
import TablePager from './TablePager';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';
import type { SubmissionDetailsTotals } from '../crewingAnalyticsApi';

export interface ExecutionSubmissionDetailsRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  submissions: number;
  modificationCount: number;
  lastSubmitted: string;
  lastModified: string;
}

interface ExecutionSubmissionDetailsTableProps {
  title: string;
  totalModificationCount?: number;
  rows: ExecutionSubmissionDetailsRow[];
  selectedColumnKeys?: K[];
  /** Called with the newly-selected dimension keys so the caller can re-fetch server-side grouped rows. */
  onColumnsChange?: (dimensionKeys: string[]) => void;
  /** Aggregate metrics across the FULL filtered result set (backend `totals`) — render this
   * directly for the grand-total row, never recompute it from the current page's `rows`. */
  backendTotals?: SubmissionDetailsTotals | null;
  /** Full filtered grouped-row count (backend `total`), used by the pager and footnote. */
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  pageLoading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Server-side sort state (backend `sortBy`/`sortDirection`) — rows arrive pre-sorted, so this
   * table no longer sorts client-side. `sortBy` is null/undefined for the default grouping order. */
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  /** Called with the clicked column's key — caller re-fetches page 0 with the new sort applied. */
  onSortChange?: (columnKey: string) => void;
}

type K = keyof ExecutionSubmissionDetailsRow;

interface C {
  key: K;
  label: string;
  width: 'wide' | 'std' | 'num';
  numeric?: boolean;
  link?: boolean;
}

const WIDE = 220;
const STD = 140;
const NUM = 210;

const COLS: C[] = [
  { key: 'region', label: 'Region / Unit', width: 'wide' },
  { key: 'market', label: 'Market / POD', width: 'wide' },
  { key: 'location', label: 'Location', width: 'wide', link: true },
  { key: 'year', label: 'Year', width: 'std' },
  { key: 'period', label: 'Period', width: 'std' },
  { key: 'week', label: 'Week', width: 'std' },
  { key: 'submissions', label: 'Submissions', width: 'num', numeric: true },
  { key: 'modificationCount', label: 'Modification Count', width: 'num', numeric: true },
  { key: 'lastSubmitted', label: 'Last Submitted', width: 'num' },
  { key: 'lastModified', label: 'Last Modified', width: 'num' },
];

// Backend supports grouping this endpoint by region/market/location/year/period/week — submissions,
// modificationCount, lastSubmitted, and lastModified always display as metrics.
const DIMENSION_KEYS: K[] = ['region', 'market', 'location', 'year', 'period', 'week'];
// Per the user story: defaults to Location, Period, Week (not every dimension).
const DEFAULT_SELECTED_KEYS: K[] = ['location', 'period', 'week'];
const METRIC_DEFS = COLS.filter((c) => !DIMENSION_KEYS.includes(c.key));

const COLUMN_GROUPS: ColumnPickerGroup[] = [
  {
    key: 'location-hierarchy',
    label: 'Location Hierarchy',
    options: [
      { key: 'region', label: 'Region / Unit' },
      { key: 'market', label: 'Market / POD' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    options: [
      { key: 'year', label: 'Year' },
      { key: 'period', label: 'Period' },
      { key: 'week', label: 'Week' },
    ],
  },
];

const n = (v: number) => v.toLocaleString();

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Normalizes MM/DD/YY, MM/DD/YYYY, and ISO (YYYY-MM-DD) inputs to the app-wide "MMM DD, YYYY" display
// format (matches AnalyticsHub's formatDateDisplay) instead of showing the raw backend string as-is.
const formatSubmissionDate = (raw: string): string => {
  if (!raw || raw === '--') return '--';
  let month: number | undefined;
  let day: number | undefined;
  let year: number | undefined;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else if (slashMatch) {
    month = Number(slashMatch[1]);
    day = Number(slashMatch[2]);
    year = Number(slashMatch[3]);
    if (year < 100) year += 2000;
  }

  if (!month || !day || !year) return raw;
  const monthStr = MONTHS[month - 1] ?? '';
  const dayStr = day < 10 ? `0${day}` : `${day}`;
  return `${monthStr} ${dayStr}, ${year}`;
};

const ExecutionSubmissionDetailsTable: React.FC<ExecutionSubmissionDetailsTableProps> = ({
  title,
  totalModificationCount,
  rows,
  selectedColumnKeys: controlledSelectedColumnKeys,
  onColumnsChange,
  backendTotals,
  total,
  page = 0,
  pageSize = 25,
  onPageChange,
  pageLoading,
  pageSizeOptions,
  onPageSizeChange,
  sortBy,
  sortDirection = 'asc',
  onSortChange,
}) => {
  const [open, setOpen] = useState(false);
  const columnPickerWrapRef = useRef<View>(null);
  useCloseOnOutsideClick(columnPickerWrapRef, () => setOpen(false), open);
  const [selected, setSelected] = useState<K[]>(controlledSelectedColumnKeys ?? DEFAULT_SELECTED_KEYS);

  useEffect(() => {
    if (controlledSelectedColumnKeys) {
      setSelected(controlledSelectedColumnKeys);
    }
  }, [controlledSelectedColumnKeys]);

  const handleColumnsChange = (keys: K[]) => {
    setSelected(keys);
    onColumnsChange?.(keys);
  };
  const cols = useMemo(
    () => [...COLS.filter((c) => DIMENSION_KEYS.includes(c.key) && selected.includes(c.key)), ...METRIC_DEFS],
    [selected],
  );

  // Every column rendered by this table is an allowed sortBy key per the backend contract.
  const isSortable = (key: K) => !!onSortChange;

  const minWidth = useMemo(() => cols.reduce((s, c) => s + (c.width === 'wide' ? WIDE : c.width === 'num' ? NUM : STD), 0), [cols]);

  const w = (kind: C['width']) => (kind === 'wide' ? styles.wide : kind === 'num' ? styles.num : styles.std);

  return (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <View style={styles.top}>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.wrap} ref={columnPickerWrapRef}>
            <TouchableOpacity style={styles.pill} onPress={() => setOpen((p) => !p)}>
              <Text style={styles.pillText}>{`Columns (${selected.length})`}</Text>
            </TouchableOpacity>
            {open && (
              <GroupedColumnPickerPanel
                groups={COLUMN_GROUPS}
                selectedKeys={selected}
                onChange={(keys) => handleColumnsChange(keys as K[])}
              />
            )}
          </View>
        </View>
        {typeof totalModificationCount === 'number' ? (
          <View style={styles.countPill}>
            <Text style={styles.countText}>{`Total Modification Count: ${n(totalModificationCount)}`}</Text>
          </View>
        ) : null}
      </View>

      <CrewingHorizontalScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scroll}>
        <View style={[styles.table, { minWidth }]}>
          <View style={styles.headRow}>
            {cols.map((c) => {
              const isActive = sortBy === c.key;
              const sortable = isSortable(c.key);
              return (
                <TouchableOpacity
                  key={c.key}
                  style={[styles.headCell, w(c.width), c.width === 'num' && styles.headCellNum]}
                  onPress={sortable ? () => onSortChange!(c.key) : undefined}
                  disabled={!sortable}
                  activeOpacity={sortable ? 0.7 : 1}
                >
                  <Text
                    style={[styles.headCellText, c.width === 'num' && styles.headCellTextNum, isActive && styles.headCellTextActive]}
                    numberOfLines={2}
                  >
                    {c.label}
                  </Text>
                  {sortable && (
                    <MaterialIcons
                      name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                      size={14}
                      color={isActive ? '#034895' : '#9CA3AF'}
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.total}>
            {cols.map((c, i) => {
              let text = '--';
              if (i === 0) text = 'Total';
              if (c.key === 'submissions') text = backendTotals ? n(backendTotals.submissions) : '--';
              if (c.key === 'modificationCount') text = backendTotals ? n(backendTotals.modificationCount) : '--';
              return <Text key={`${c.key}-t-${i}`} style={[styles.totalCell, w(c.width)]}>{text}</Text>;
            })}
          </View>

          <View style={styles.tableViewport}>
            <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
              {rows.map((r, i) => (
                <View key={`${r.location}-${r.week}-${i}`} style={styles.row}>
                  {cols.map((c) => {
                    const raw = r[c.key] as string | number;
                    const text = c.numeric
                      ? n(Number(raw))
                      : (c.key === 'lastSubmitted' || c.key === 'lastModified')
                        ? formatSubmissionDate(String(raw))
                        : String(raw);
                    return <Text key={`${c.key}-${i}`} style={[styles.cell, w(c.width), c.link ? styles.link : null]}>{text}</Text>;
                  })}
                </View>
              ))}
            </CrewingVerticalScrollView>
          </View>
        </View>
      </CrewingHorizontalScrollView>

      <Text style={styles.footer}>{`${(total ?? rows.length).toLocaleString()} groups total${backendTotals ? ` from ${n(backendTotals.submissions)} submissions` : ''} in the applied scope.`}</Text>
      {onPageChange && (
        <TablePager page={page} pageSize={pageSize} total={total ?? rows.length} onPageChange={onPageChange} loading={pageLoading} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: { backgroundColor: '#FFFFFF', borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB', padding: 14, overflow: 'visible' },
  headerContainer: { flexDirection: 'column', gap: 6, marginBottom: 8, position: 'relative', zIndex: 30 },
  top: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, position: 'relative', zIndex: 30 },
  title: { fontSize: 16, fontFamily: FontFamily.bold, fontWeight: '700', color: '#1A1D21' },
  rightTop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  countPill: { borderRadius: 14, backgroundColor: '#F3F4F6', paddingHorizontal: 10, paddingVertical: 4 },
  countText: { fontSize: 10, color: '#4B5563', fontWeight: '600' },
  wrap: { position: 'relative', zIndex: 40 },
  pill: { borderWidth: 1, borderColor: '#BFDBFE', backgroundColor: '#EFF6FF', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 },
  pillText: { fontSize: 11, color: '#0B5FC6', fontWeight: '600' },
  scroll: { flexGrow: 1 },
  table: { width: '100%' },
  tableViewport: { flex: 1, minHeight: 260, maxHeight: 420 },
  headRow: { flexDirection: 'row', alignItems: 'stretch', borderBottomWidth: 2, borderBottomColor: '#D7E5F8', backgroundColor: '#FFFFFF', paddingVertical: 8 },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#D7E5F8', paddingVertical: 8 },
  total: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#D0D0D0', borderBottomWidth: 2, borderBottomColor: '#D7E5F8', backgroundColor: '#F3F7FD', paddingVertical: 9 },
  headCell: { flexDirection: 'row', alignItems: 'flex-start', gap: 2, paddingHorizontal: 6 },
  headCellNum: { justifyContent: 'flex-end' },
  headCellText: { flex: 1, fontSize: 10, fontFamily: FontFamily.bold, color: '#616161', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  headCellTextNum: { flex: 0, textAlign: 'right' },
  headCellTextActive: { color: '#034895' },
  cell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.medium, color: '#3A3A3A' },
  totalCell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.bold, color: '#2D2D2D', fontWeight: '700' },
  wide: { flexGrow: 1, flexBasis: WIDE, minWidth: WIDE },
  std: { flexGrow: 1, flexBasis: STD, minWidth: STD },
  num: { flexGrow: 1, flexBasis: NUM, minWidth: NUM, textAlign: 'right' },
  link: { color: '#0B5FC6', fontWeight: '600' },
  footer: { marginTop: 10, fontSize: 12, fontFamily: FontFamily.regular, color: '#6B7280', lineHeight: 17 },
});

export default ExecutionSubmissionDetailsTable;
